import { Paper } from "@mui/material";
import { GridApi, RowClassParams, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { Disposition } from "@/pages/mill/schedule/components/mechanical-results/types";
import { useMillHeatsTableData } from "../hooks/useMillHeatsTableData";
import { MillHeatRow } from "../repositories/LabDashboardRepository";

type Props = {
	rows: MillHeatRow[];
	isLoading: boolean;
	selectedCampaignId: number | null;
	isEditingDisposition?: boolean;
	savingHeatId?: number | null;
	onSelectHeat: (heat: MillHeatRow, editingDisposition: boolean) => void;
	onChangeDisposition?: (heatId: number, value: Disposition) => void;
};

export const MillHeatsTable = ({
	rows,
	isLoading,
	selectedCampaignId,
	isEditingDisposition = false,
	savingHeatId = null,
	onSelectHeat,
	onChangeDisposition,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const editingCampaignId = isEditingDisposition ? selectedCampaignId : null;

	const { columnDefs } = useMillHeatsTableData();
	const { defaultColDef } = useGridDefinitions({});

	const getRowStyle = (
		params: RowClassParams<MillHeatRow>,
	): RowStyle | undefined =>
		params.data?.CampaignId === selectedCampaignId
			? { backgroundColor: "#e3f2fd" }
			: undefined;

	useEffect(() => {
		gridApi?.redrawRows();
	}, [gridApi, selectedCampaignId, savingHeatId, editingCampaignId]);

	return (
		<Paper
			elevation={2}
			sx={{
				height: "100%",
				width: "100%",
				"& .ag-cell": { fontSize: 11 },
			}}
		>
			<AgGridReact<MillHeatRow>
				loading={isLoading}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowHeight={32}
				headerHeight={34}
				animateRows
				loadingOverlayComponent={CenteredLazyLoading}
				getRowId={(params) => String(params.data.CampaignId)}
				getRowStyle={getRowStyle}
				context={{
					editingCampaignId,
					savingHeatId,
					onChangeDisposition,
				}}
				onGridReady={(params) => setGridApi(params.api)}
				onCellClicked={(event) =>
					event.data &&
					onSelectHeat(event.data, event.colDef.field === "SampleStatusCode")
				}
			/>
		</Paper>
	);
};
